<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="8" lg="auto">
        <v-radio-group v-model="type" row hide-details>
          <v-radio :label="$t('orders')" value="orders"></v-radio>
          <v-radio :label="$t('meals_main')" value="foods"></v-radio>
        </v-radio-group>
      </v-col>
      <v-col cols="4" lg="auto">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>

    <div v-else-if="report.length">
      <v-data-table
        :headers="getMobileHeaders(headers)"
        :items="customSearch(total, headers, filters.search)"
        :items-per-page="-1"
        class="striped-table ordera-box-shadow"
        hide-default-footer
        disable-pagination
        mobile-breakpoint="0"
      >
        <template v-slot:no-data>
          <v-container class="text-center py-12">
            <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-food-off </v-icon>
            <div class="text-h6 grey--text text--darken-1 mb-2">
              {{ $t("no_taken_group_meals_data") }}
            </div>
            <div class="text-body-2 grey--text">
              {{ $t("select_date_and_filters") }}
            </div>
          </v-container>
        </template>
        <template v-slot:item.num="{ item, index }">
          <span>{{ index + 1 }}</span>
        </template>
        <template v-slot:item.menu="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                {{ item.menu }}
              </span>
            </template>
            <span>{{ item.id }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.shift_one="{ item }">
          <span>
            {{ [item.shift_one.quantity, item.shift_one.payed].join("/") }}
          </span>
        </template>
        <template v-slot:item.shift_two="{ item }">
          <span>
            {{ [item.shift_two.quantity, item.shift_two.payed].join("/") }}
          </span>
        </template>
        <template v-slot:item.shift_three="{ item }">
          <span>
            {{ [item.shift_three.quantity, item.shift_three.payed].join("/") }}
          </span>
        </template>
        <template v-slot:item.shift_four="{ item }">
          <span>
            {{ [item.shift_four.quantity, item.shift_four.payed].join("/") }}
          </span>
        </template>
        <template v-slot:item.total="{ item }">
          <span>{{ [item.total.quantity, item.total.payed].join("/") }}</span>
        </template>
        <template v-slot:item.difference="{ item }">
          <span>{{ item.total.quantity - item.total.payed }}</span>
        </template>
        <template v-slot:item.month="{ item }">
          <v-row no-gutters>
            <circle-button
              v-if="String(item.id).toLowerCase() !== $t('total').toLowerCase()"
              @click="openDialogReportFirm(item)"
              icon="mdi-format-list-bulleted"
              color="primary"
              :tooltip="$t('tooltip_monthly_report')"
            />
            <circle-button
              v-if="String(item.id).toLowerCase() !== $t('total').toLowerCase()"
              @click="openCompanyEmployees(item)"
              icon="mdi-account-group"
              color="primary"
              :tooltip="$t('company_employees_orders')"
            />
          </v-row>
        </template>
        <template v-slot:body.append>
          <tr>
            <td v-if="!device.isMobile"></td>
            <td class="font-weight-bold">{{ $t("total") }}</td>
            <td v-if="selectedShifts.includes(1)" class="font-weight-bold">
              {{ [totals.shift_one.quantity, totals.shift_one.payed].join("/") }}
            </td>
            <td v-if="selectedShifts.includes(2)" class="font-weight-bold">
              {{ [totals.shift_two.quantity, totals.shift_two.payed].join("/") }}
            </td>
            <td v-if="selectedShifts.includes(3)" class="font-weight-bold">
              {{ [totals.shift_three.quantity, totals.shift_three.payed].join("/") }}
            </td>
            <td v-if="selectedShifts.includes(4)" class="font-weight-bold">
              {{ [totals.shift_four.quantity, totals.shift_four.payed].join("/") }}
            </td>
            <td class="font-weight-bold">
              {{ [totals.total.quantity, totals.total.payed].join("/") }}
            </td>
            <td>
              {{ totals.total.quantity - totals.total.payed }}
            </td>
            <td></td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <report-group-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :firm-report-item="firmReportItem"
    ></report-group-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import ReportGroupMealsTakenCompanyMonthlyOrders from "@/components/report/group-meals/dialogs/ReportGroupMealsTakenCompanyMonthlyOrders.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasTakenReport from "./taken/mixins/hasTakenReport";
import hasReloadWatch from "@/mixins/report/hasReloadWatch";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";

export default {
  name: "ReportMealsTabTaken",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasTakenReport, hasReloadWatch, hasMobileHeaders],
  components: {
    ReportGroupMealsTakenCompanyMonthlyOrders,
  },
  data: () => ({
    loading: false,
    dialogFirm: false,
    firmReportItem: null,
    type: "orders",
    selectedShifts: [1, 2, 3, 4],
  }),
  methods: {
    openDialogReportFirm(item) {
      this.dialogFirm = true;
      this.firmReportItem = item;
    },
    openCompanyEmployees(item) {
      // Set the company in store
      const company = {
        id: item.company_id,
        name: item.menu,
        company: item.menu,
      };
      this.$store.commit('report/SET_SELECTED_COMPANY', company);
      
      // Emit event to parent to switch to Employees tab
      this.$emit('switch-tab', 1); // Tab index 1 is Employees
    },
  },
  computed: {
    report() {
      const orderIds = new Set(this.reportItem.map((item) => item.id));
      const singleOrders =
        this.type === "foods"
          ? this.reportItem
          : [...orderIds].map((id) => this.reportItem.find((item) => item.id === id));
      const companyIds = new Set(singleOrders.map((item) => item.company_id));
      return [...companyIds].map((id) => {
        const companyOrders = singleOrders.filter((item) => item.company_id === id);

        const calculateShiftOrders = (shift) => {
          if (!this.selectedShifts.includes(shift)) {
            return { quantity: 0, payed: 0 };
          }
          const shiftOrders = companyOrders.filter((item) => item.shift === shift);
          return {
            quantity: shiftOrders.reduce((sum, item) => sum + item.quantity, 0),
            payed: shiftOrders.reduce((sum, item) => sum + item.quantity_payed, 0),
          };
        };

        const filteredOrders = companyOrders.filter((item) =>
          this.selectedShifts.includes(item.shift)
        );

        return {
          company_id: id,
          menu: this.toLatin(companyOrders[0].company_name),
          shift_one: calculateShiftOrders(1),
          shift_two: calculateShiftOrders(2),
          shift_three: calculateShiftOrders(3),
          shift_four: calculateShiftOrders(4),
          total: {
            quantity: filteredOrders.reduce((sum, item) => sum + item.quantity, 0),
            payed: filteredOrders.reduce((sum, item) => sum + item.quantity_payed, 0),
          },
        };
      });
    },
    total() {
      return (this.report || []).map((item, index) => {
        return {
          num: index + 1,
          ...item,
        };
      });
    },
    filters() {
      return this.$store.state.report.filters;
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("firm"), value: "menu" },
      ];

      const shiftHeaders = [];
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("1st_shift"), value: "shift_one" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("2nd_shift"), value: "shift_two" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("3rd_shift"), value: "shift_three" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("4th_shift"), value: "shift_four" });
      }

      const endHeaders = [
        { text: this.$t("total"), value: "total" },
        { text: this.$t("difference"), value: "difference" },
        { text: this.$t("months"), value: "month" },
      ];

      return [...baseHeaders, ...shiftHeaders, ...endHeaders];
    },
    totals() {
      // Calculate totals for all visible items
      const items = this.customSearch(this.total, this.headers, this.filters.search);
      const sum = {
        menu: this.$t("total"),
        shift_one: { payed: 0, quantity: 0 },
        shift_two: { payed: 0, quantity: 0 },
        shift_three: { payed: 0, quantity: 0 },
        shift_four: { payed: 0, quantity: 0 },
        total: { payed: 0, quantity: 0 },
      };
      items.forEach((item) => {
        if (item.shift_one && this.selectedShifts.includes(1)) {
          sum.shift_one.payed += Number(item.shift_one.payed || 0);
          sum.shift_one.quantity += Number(item.shift_one.quantity || 0);
        }
        if (item.shift_two && this.selectedShifts.includes(2)) {
          sum.shift_two.payed += Number(item.shift_two.payed || 0);
          sum.shift_two.quantity += Number(item.shift_two.quantity || 0);
        }
        if (item.shift_three && this.selectedShifts.includes(3)) {
          sum.shift_three.payed += Number(item.shift_three.payed || 0);
          sum.shift_three.quantity += Number(item.shift_three.quantity || 0);
        }
        if (item.shift_four && this.selectedShifts.includes(4)) {
          sum.shift_four.payed += Number(item.shift_four.payed || 0);
          sum.shift_four.quantity += Number(item.shift_four.quantity || 0);
        }
        if (item.total) {
          sum.total.payed += Number(item.total.payed || 0);
          sum.total.quantity += Number(item.total.quantity || 0);
        }
      });
      return sum;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.fetchTakenReport({
          date: this.filters.dates.from,
          date2: this.filters.dates.to,
        });
      },
      deep: true,
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        this.printDocument({
          title: [this.$t("report"), this.$t("taken")].join(": "),
          tables: [
            {
              headers: this.headers.filter((header) => header.value !== "month"),
              items: this.customSearch(
                [...this.total, this.totals].map((item) => ({
                  ...item,
                  shift_one: item.shift_one
                    ? [item.shift_one.payed, item.shift_one.quantity].join("/")
                    : "",
                  shift_two: item.shift_two
                    ? [item.shift_two.payed, item.shift_two.quantity].join("/")
                    : "",
                  shift_three: item.shift_three
                    ? [item.shift_three.payed, item.shift_three.quantity].join("/")
                    : "",
                  shift_four: item.shift_four
                    ? [item.shift_four.payed, item.shift_four.quantity].join("/")
                    : "",
                  total: item.total
                    ? [item.total.payed, item.total.quantity].join("/")
                    : "",
                  difference: item.total ? item.total.quantity - item.total.payed : 0,
                })),
                this.headers,
                this.filters.search
              ),
              showCaption: true,
            },
          ],
          hideDatetime: true,
          subtitle: this.subtitle,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("taken")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("taken"),
              headers: this.headers.filter((header) => header.value !== "month"),
              items: this.customSearch(
                [...this.total, this.totals].map((item) => ({
                  ...item,
                  shift_one: item.shift_one
                    ? [item.shift_one.payed, item.shift_one.quantity].join("/")
                    : "",
                  shift_two: item.shift_two
                    ? [item.shift_two.payed, item.shift_two.quantity].join("/")
                    : "",
                  shift_three: item.shift_three
                    ? [item.shift_three.payed, item.shift_three.quantity].join("/")
                    : "",
                  shift_four: item.shift_four
                    ? [item.shift_four.payed, item.shift_four.quantity].join("/")
                    : "",
                  total: item.total
                    ? [item.total.payed, item.total.quantity].join("/")
                    : "",
                  difference: item.total ? item.total.quantity - item.total.payed : 0,
                })),
                this.headers,
                this.filters.search
              ),
              title: [this.$t("report"), this.$t("taken")].join(" "),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
